import React, { useEffect, useState, useMemo } from "react";
import { fetchTrades, fetchAdminUsers, fetchSignalCopies } from "../api/client";
import { format } from "date-fns";
import { Filter, RefreshCcw, RotateCcw, Search, X, CheckCircle, AlertCircle, AlertTriangle, TrendingUp, TrendingDown, Terminal } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const INITIAL_FILTERS = {
  user_name: "",
  license_id: "",
  magic_number: "",
  symbol: "",
  date_from: getTodayString(),
  date_to: getTodayString(),
};

const EMPTY_FILTERS = {
  user_name: "",
  license_id: "",
  magic_number: "",
  symbol: "",
  date_from: "",
  date_to: "",
};

export default function TradeHistory() {
  const { user: currentUser } = useAuth();
  
  const [data, setData] = useState({ trades: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  
  // Admin-specific state
  const [users, setUsers] = useState([]);
  const [selectedUserObj, setSelectedUserObj] = useState(null);

  // Modal-specific state for terminal copies
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [copiesList, setCopiesList] = useState([]);
  const [loadingCopies, setLoadingCopies] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // Load all users for Admin dropdown on mount
  useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchAdminUsers(0, 200)
        .then((res) => {
          setUsers(res.users || []);
        })
        .catch((err) => {
          console.error("Failed to load admin users list:", err);
        });
    }
  }, [currentUser]);

  const load = (nextPage = page, nextFilters = filters, nextPageSize = pageSize) => {
    setLoading(true);
    fetchTrades(nextPage * nextPageSize, nextPageSize, nextFilters)
      .then((res) => {
        setData(res);
        setPage(nextPage);
      })
      .catch((err) => showToast(err.response?.data?.detail || "Failed to load trade history", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(0, INITIAL_FILTERS);
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Dynamic License IDs options based on user selection
  const licenseOptions = useMemo(() => {
    if (currentUser?.role === "admin") {
      if (selectedUserObj) {
        return selectedUserObj.license_ids || [];
      } else {
        return Array.from(new Set(users.flatMap((u) => u.license_ids || [])));
      }
    } else {
      return currentUser?.license_ids || [];
    }
  }, [currentUser, users, selectedUserObj]);

  // Enforce cascading changes in Admin mode
  const handleUserChange = (userName) => {
    const matchedUser = users.find((u) => u.name === userName);
    if (matchedUser) {
      setSelectedUserObj(matchedUser);
      setFilters((prev) => ({
        ...prev,
        user_name: userName,
        license_id: matchedUser.license_ids?.includes(prev.license_id) ? prev.license_id : "",
      }));
    } else {
      setSelectedUserObj(null);
      setFilters((prev) => ({
        ...prev,
        user_name: "",
      }));
    }
  };

  const handleLicenseChange = (licenseId) => {
    if (currentUser?.role === "admin" && licenseId) {
      const matchedUser = users.find((u) => u.license_ids?.includes(licenseId));
      if (matchedUser) {
        setSelectedUserObj(matchedUser);
        setFilters((prev) => ({
          ...prev,
          license_id: licenseId,
          user_name: matchedUser.name,
        }));
        return;
      }
    }
    updateFilter("license_id", licenseId);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    load(0, filters);
  };

  const resetFilters = () => {
    setSelectedUserObj(null);
    setFilters(EMPTY_FILTERS);
    load(0, EMPTY_FILTERS);
  };

  const renderDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return format(new Date(dateStr), "yyyy.MM.dd HH:mm:ss");
    } catch {
      return "—";
    }
  };

  const openCopiesModal = (trade) => {
    setSelectedSignal(trade);
    setLoadingCopies(true);
    setCopiesList([]);
    
    // Fallback to trade ID if signal_id is missing (standard user trade logs use signal_id)
    const signalId = trade.signal_id || trade.id;
    
    fetchSignalCopies(signalId)
      .then((res) => {
        setCopiesList(res.copies || []);
      })
      .catch((err) => {
        showToast(err.response?.data?.detail || "Failed to load execution copies", "error");
      })
      .finally(() => {
        setLoadingCopies(false);
      });
  };

  const closeModal = () => {
    setSelectedSignal(null);
    setCopiesList([]);
  };

  // Check if cascading other filters should be disabled in Admin view
  const isAdminAndNoUser = currentUser?.role === "admin" && !selectedUserObj;

  // Calculate copies stats
  const totalCopies = copiesList.length;
  const closedCopies = copiesList.filter((c) => c.status === "closed").length;
  const openCopies = copiesList.filter((c) => c.status === "open").length;
  const errorCopies = copiesList.filter((c) => c.status === "error").length;
  const totalPnL = copiesList.reduce((acc, c) => acc + (c.profit || 0), 0);

  return (
    <div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      <div className="page-header">
        <div>
          <div className="page-title">Trade History</div>
          <div className="page-sub">
            {currentUser?.role === "admin" 
              ? `${data.total} master TradingView signals received` 
              : `${data.total} individual terminal copies recorded`}
          </div>
        </div>
        <button className="btn btn-ghost" onClick={() => load()} disabled={loading}>
          <RefreshCcw size={15} />
          Refresh
        </button>
      </div>

      <div className="card filter-card" style={{ marginBottom: "16px" }}>
        <div 
          className="card-header" 
          onClick={() => setShowFilters(!showFilters)} 
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={14} style={{ color: "var(--accent)" }} />
            <span style={{ fontSize: "14px", fontWeight: "700" }}>Filters & Search</span>
          </div>
          <button 
            type="button" 
            className="btn btn-ghost" 
            style={{ padding: "4px 10px", fontSize: "11px", minHeight: "auto", borderRadius: "8px", border: "1px solid var(--border)" }}
          >
            {showFilters ? "Collapse" : "Expand Filters"}
          </button>
        </div>
        
        {showFilters && (
          <form onSubmit={applyFilters} style={{ padding: "14px" }}>
            <div className="filter-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", padding: 0 }}>
              
              {/* User Filter Dropdown (Admin-only) */}
              {currentUser?.role === "admin" ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: "11px" }}>User</label>
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.user_name}
                    onChange={(e) => handleUserChange(e.target.value)}
                  >
                    <option value="">Select User</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Cascading Dependent License ID Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>License ID</label>
                {currentUser?.role === "admin" ? (
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.license_id}
                    onChange={(e) => handleLicenseChange(e.target.value)}
                  >
                    <option value="">All Licenses</option>
                    {licenseOptions.map((lic) => (
                      <option key={lic} value={lic}>
                        {lic}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.license_id}
                    onChange={(e) => updateFilter("license_id", e.target.value)}
                  >
                    <option value="">All Licenses</option>
                    {(currentUser?.license_ids || []).map((lic) => (
                      <option key={lic} value={lic}>
                        {lic}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Magic Number Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Magic No</label>
                <input
                  className="form-input"
                  style={{ padding: "8px 12px", fontSize: "13px" }}
                  type="number"
                  value={filters.magic_number}
                  onChange={(e) => updateFilter("magic_number", e.target.value)}
                  placeholder="123456"
                />
              </div>

              {/* Symbol Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Symbol</label>
                <div className="input-with-icon">
                  <Search size={12} style={{ left: "12px" }} />
                  <input
                    className="form-input"
                    style={{ padding: "8px 12px 8px 34px", fontSize: "13px" }}
                    value={filters.symbol}
                    onChange={(e) => updateFilter("symbol", e.target.value)}
                    placeholder="XAUUSD"
                  />
                </div>
              </div>

              {/* Date range row + filter action buttons in a single full-width line */}
              <div style={{ 
                gridColumn: "1 / -1", 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "12px", 
                alignItems: "flex-end", 
                justifyContent: "space-between", 
                background: "rgba(255, 255, 255, 0.01)", 
                padding: "12px 0 0 0", 
                borderTop: "1px solid rgba(255, 255, 255, 0.04)",
                marginTop: "4px"
              }}>
                <div style={{ display: "flex", gap: "12px", flex: "1", minWidth: "240px" }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: "1" }}>
                    <label className="form-label" style={{ fontSize: "11px" }}>From Date</label>
                    <input
                      className="form-input"
                      style={{ padding: "8px 12px", fontSize: "13px" }}
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => updateFilter("date_from", e.target.value)}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: 0, flex: "1" }}>
                    <label className="form-label" style={{ fontSize: "11px" }}>To Date</label>
                    <input
                      className="form-input"
                      style={{ padding: "8px 12px", fontSize: "13px" }}
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => updateFilter("date_to", e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button className="btn btn-primary" type="submit" style={{ padding: "8px 16px", fontSize: "12px" }} disabled={loading || isAdminAndNoUser}>
                    <Filter size={13} />
                    Apply
                  </button>
                  <button className="btn btn-ghost" type="button" style={{ padding: "8px 16px", fontSize: "12px" }} onClick={resetFilters} disabled={loading}>
                    <RotateCcw size={13} />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Main Trade History Table */}
      <div className="card">
        {loading ? (
          <div className="loading">Loading trade history…</div>
        ) : data.trades.length === 0 ? (
          <div className="empty">No trades match the selected filter criteria.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr style={{ background: "rgba(0, 242, 254, 0.03)" }}>
                  <th style={{ width: "60px" }}>S.No</th>
                  <th>Symbol</th>
                  <th>Entry Time</th>
                  <th>Entry Price</th>
                  <th>Exit Time</th>
                  <th>Exit Price</th>
                  <th>Trade Type</th>
                  <th>User</th>
                  <th>License ID</th>
                  <th>Magic No</th>
                  <th style={{ textAlign: "right" }}>PnL</th>
                  <th style={{ textAlign: "center" }}>Terminal ID</th>
                  <th style={{ textAlign: "center", width: "110px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.trades.map((t, idx) => (
                  <tr key={t.id || idx}>
                    <td style={{ color: "var(--text2)", fontWeight: 500 }}>{idx + 1}</td>
                    <td style={{ fontWeight: 700, color: "var(--text)" }}>
                      {t.symbol}
                      {t.status === "open" && (
                        <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: "var(--green)", marginLeft: "6px", boxShadow: "0 0 8px var(--green)" }} title="Active Open Trade" />
                      )}
                      {t.status === "error" && (
                        <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: "var(--red)", marginLeft: "6px", boxShadow: "0 0 8px var(--red)" }} title={`Execution Failed: ${t.error}`} />
                      )}
                    </td>
                    <td style={{ color: "var(--text2)", fontSize: "12px" }}>
                      {renderDate(t.opened_at)}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {t.open_price != null ? t.open_price.toFixed(5).replace(/\.?0+$/, "") : "—"}
                    </td>
                    <td style={{ color: "var(--text2)", fontSize: "12px" }}>
                      {renderDate(t.closed_at)}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {t.close_price != null ? t.close_price.toFixed(5).replace(/\.?0+$/, "") : "—"}
                    </td>
                    <td>
                      <span className={`badge ${t.action?.toUpperCase().includes("BUY") ? "badge-buy" : "badge-sell"}`}>
                        {t.action}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{t.user_name || "—"}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text2)" }}>{t.license_id || "—"}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{t.magic_number || "—"}</td>
                    <td style={{ 
                      textAlign: "right", 
                      fontWeight: 700,
                      color: t.status === "error" ? "var(--red)" : t.profit > 0 ? "var(--green)" : t.profit < 0 ? "var(--red)" : "var(--text2)"
                    }}>
                      {t.status === "error" ? (
                        <span style={{ fontSize: "11px", fontWeight: "normal", background: "rgba(239, 68, 68, 0.1)", padding: "2px 6px", borderRadius: "4px" }}>
                          FAIL
                        </span>
                      ) : t.profit != null && t.status === "closed" ? (
                        (t.profit >= 0 ? "+" : "") + t.profit.toFixed(2)
                      ) : t.status === "open" ? (
                        <span style={{ fontSize: "11px", color: "var(--accent)" }}>OPEN</span>
                      ) : "—"}
                    </td>
                    <td style={{ 
                      fontFamily: "var(--mono)", 
                      fontSize: "11px", 
                      textAlign: "center",
                      color: t.terminal_id?.includes("Master") ? "var(--accent)" : "var(--text2)" 
                    }}>
                      {t.terminal_id || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button 
                        className="btn btn-ghost" 
                        onClick={() => openCopiesModal(t)}
                        style={{ padding: "4px 8px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                        title="View execution details across client terminals"
                      >
                        📋 Copies
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.total > 0 && (
          <div className="pagination-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "var(--text2)", fontSize: "13px" }}>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = parseInt(e.target.value, 10);
                  setPageSize(newSize);
                  load(0, filters, newSize);
                }}
                className="form-input"
                style={{
                  width: "80px",
                  padding: "6px 24px 6px 12px",
                  fontSize: "13px",
                  height: "34px",
                  borderRadius: "8px",
                  minHeight: "auto",
                  border: "1px solid var(--border)",
                  background: "var(--sidebar-user-bg)"
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button className="btn btn-ghost" onClick={() => load(page - 1)} disabled={loading || page === 0} style={{ padding: "6px 12px", minHeight: "auto" }}>
                Previous
              </button>
              <span style={{ display: "inline-flex", alignItems: "center", color: "var(--text2)", fontSize: "13px", padding: "0 10px", fontWeight: "700" }}>
                Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))}
              </span>
              <button className="btn btn-ghost" onClick={() => load(page + 1)} disabled={loading || (page + 1) * pageSize >= data.total} style={{ padding: "6px 12px", minHeight: "auto" }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Glassmorphic Modal for Copied Terminal Logs */}
      {selectedSignal && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="user-modal" style={{ width: "min(1080px, 100%)", backdropFilter: "blur(20px)" }}>
            <div className="modal-header" style={{ background: "rgba(10, 11, 24, 0.4)" }}>
              <div>
                <div className="modal-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Terminal size={20} style={{ color: "var(--accent)" }} />
                  Copied Terminals Execution Logs
                </div>
                <div className="modal-sub">
                  Real-time status reports, entries, exits, and execution logs from MT5 EAs matching Signal ID: {selectedSignal.signal_id || selectedSignal.id}
                </div>
              </div>
              <button className="icon-btn" type="button" onClick={closeModal} title="Close Modal">
                <X size={18} />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: "24px", background: "rgba(10, 11, 24, 0.2)" }}>
              {loadingCopies ? (
                <div className="loading" style={{ padding: "40px 0" }}>Querying active terminal logs...</div>
              ) : (
                <>
                  {/* Premium Metrics Summary Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px", marginBottom: "24px" }}>
                    
                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Symbol</div>
                      <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)", marginTop: "4px" }}>{selectedSignal.symbol}</div>
                    </div>

                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Signal Type</div>
                      <div style={{ marginTop: "4px" }}>
                        <span className={`badge ${selectedSignal.action?.toUpperCase().includes("BUY") ? "badge-buy" : "badge-sell"}`}>
                          {selectedSignal.action}
                        </span>
                      </div>
                    </div>

                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Total Copies</div>
                      <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--accent)", marginTop: "4px" }}>{totalCopies}</div>
                    </div>

                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Active / Closed</div>
                      <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--green)", marginTop: "4px", display: "flex", justifyContent: "center", gap: "6px" }}>
                        <span title="Open Status">{openCopies}</span>
                        <span style={{ color: "var(--text2)", fontWeight: 300 }}>/</span>
                        <span style={{ color: "#a5b4fc" }} title="Closed Status">{closedCopies}</span>
                      </div>
                    </div>

                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Failures</div>
                      <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--red)", marginTop: "4px" }}>{errorCopies}</div>
                    </div>

                    <div style={{ background: "var(--sidebar-user-bg)", padding: "12px", borderRadius: "10px", border: "1px solid var(--border)", textAlign: "center" }}>
                      <div style={{ fontSize: "10px", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Net Copy PnL</div>
                      <div style={{ 
                        fontSize: "18px", 
                        fontWeight: "800", 
                        color: totalPnL > 0 ? "var(--green)" : totalPnL < 0 ? "var(--red)" : "var(--text2)", 
                        marginTop: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px"
                      }}>
                        {totalPnL > 0 ? <TrendingUp size={16} /> : totalPnL < 0 ? <TrendingDown size={16} /> : null}
                        {totalPnL >= 0 ? "+" : ""}{totalPnL.toFixed(2)}
                      </div>
                    </div>

                  </div>

                  {copiesList.length === 0 ? (
                    <div className="empty" style={{ background: "var(--pagination-bg)", borderRadius: "8px", border: "1px dashed var(--border)" }}>
                      No individual terminal executions recorded for this master signal.
                    </div>
                  ) : (
                    <div className="table-scroll" style={{ maxHeight: "360px", border: "1px solid var(--border)", borderRadius: "8px" }}>
                      <table style={{ margin: 0 }}>
                        <thead>
                          <tr style={{ background: "rgba(255, 255, 255, 0.02)", position: "sticky", top: 0, zIndex: 1 }}>
                            <th>Terminal ID</th>
                            <th>Status</th>
                            <th>Trade Type</th>
                            <th style={{ textAlign: "right" }}>Entry Price</th>
                            <th style={{ textAlign: "right" }}>Exit Price</th>
                            <th>Opened At</th>
                            <th>Closed At</th>
                            <th style={{ textAlign: "right" }}>PnL (Lot)</th>
                            <th>Execution Details / Error Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {copiesList.map((copy, cidx) => (
                            <tr key={copy.id || cidx}>
                              <td style={{ fontWeight: 700, color: "var(--accent)" }}>
                                {copy.terminal_id || "—"}
                              </td>
                              <td>
                                <span className={`badge ${
                                  copy.status === "open" 
                                    ? "badge-pending" 
                                    : copy.status === "closed" 
                                      ? "badge-ok" 
                                      : "badge-error"
                                }`}>
                                  {copy.status?.toUpperCase()}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${copy.action?.toUpperCase().includes("BUY") ? "badge-buy" : "badge-sell"}`}>
                                  {copy.action}
                                </span>
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 500 }}>
                                {copy.open_price != null ? copy.open_price.toFixed(5).replace(/\.?0+$/, "") : "—"}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 500 }}>
                                {copy.close_price != null ? copy.close_price.toFixed(5).replace(/\.?0+$/, "") : "—"}
                              </td>
                              <td style={{ fontSize: "11px", color: "var(--text2)" }}>
                                {renderDate(copy.opened_at)}
                              </td>
                              <td style={{ fontSize: "11px", color: "var(--text2)" }}>
                                {renderDate(copy.closed_at)}
                              </td>
                              <td style={{ 
                                textAlign: "right", 
                                fontWeight: 700, 
                                color: copy.profit > 0 ? "var(--green)" : copy.profit < 0 ? "var(--red)" : "var(--text2)" 
                              }}>
                                {copy.status === "error" ? "—" : (copy.profit >= 0 ? "+" : "") + copy.profit.toFixed(2)}
                                {copy.status !== "error" && (
                                  <span style={{ fontSize: "10px", color: "var(--text2)", fontWeight: "normal", marginLeft: "4px" }}>
                                    ({copy.lot || 0.01})
                                  </span>
                                )}
                              </td>
                              <td style={{ 
                                fontSize: "11px", 
                                color: copy.status === "error" ? "var(--red)" : "var(--text2)",
                                fontFamily: "var(--mono)",
                                maxWidth: "250px",
                                whiteSpace: "normal",
                                wordBreak: "break-all"
                              }}>
                                {copy.status === "error" ? (
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <AlertTriangle size={12} />
                                    {copy.error || "Execution failed"}
                                  </div>
                                ) : copy.status === "closed" ? (
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--green)" }}>
                                    <CheckCircle size={12} />
                                    Successfully closed
                                  </div>
                                ) : (
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--accent)" }}>
                                    <Terminal size={12} />
                                    Position is currently active
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="modal-footer" style={{ background: "rgba(10, 11, 24, 0.4)" }}>
              <button className="btn btn-ghost" type="button" onClick={closeModal}>
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
